import React, { useEffect } from 'react';

import { Alert, Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconInfoCircle } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { UseMutationResult } from 'react-query';
import { ErrorMessage, PayrollPeriod, PayrollPeriodUpdate } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalPayrollPeriodEditProps {
  opened: boolean;
  handleClose: () => void;
  payrollPeriod?: PayrollPeriod;
  updateMutation: UseMutationResult<
    AxiosResponse<PayrollPeriod>,
    AxiosError<ErrorMessage>,
    PayrollPeriodUpdate
  >;
}

interface FormValues {
  payrollPeriodId: string;
  startDate: Date | null;
  endDate: Date | null;
}

const ModalPayrollPeriodEdit = ({
  opened,
  handleClose,
  payrollPeriod,
  updateMutation,
}: IModalPayrollPeriodEditProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const form = useForm<FormValues>({
    initialValues: {
      payrollPeriodId: '',
      startDate: null,
      endDate: null,
    },
    validate: values => ({
      startDate: values.startDate === null ? 'A start date is required' : null,
      endDate: values.endDate === null ? 'An end date is required' : null,
    }),
  });

  const handleCloseForm = (): void => {
    form.reset();
    handleClose();
  };
  const handleSubmit = (formValues: FormValues): void => {
    const payload = {
      ...formValues,
      startDate: dayjs(formValues.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(formValues.endDate).format('YYYY-MM-DD'),
      action: null,
    };
    updateMutation.mutate(payload, {
      onError: error => {
        errorNotification('Payroll period update failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Payroll period update success.');
        handleCloseForm();
      },
    });
  };

  useShallowEffect(() => {
    if (!!payrollPeriod) {
      form.setValues({
        ...payrollPeriod,
        startDate: dayjs(payrollPeriod.startDate).toDate(),
        endDate: dayjs(payrollPeriod.endDate).toDate(),
      });
    }
  }, [payrollPeriod]);

  useEffect(() => {
    if (form.values.startDate && form.values.endDate && form.values.startDate > form.values.endDate) {
      form.setFieldValue('endDate', dayjs(form.values.startDate).add(1, 'd').toDate());
    }
  }, [form]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseForm}
      size='lg'
      title={
        <Title order={4} pr='md'>
          Edit Payroll Period
        </Title>
      }
      withinPortal
    >
      <LoadingOverlay visible={!payrollPeriod} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md'>
          <Group grow noWrap>
            <DatePickerInput
              {...form.getInputProps('startDate')}
              label='Begin Date'
              data-cy='payrollPeriodEditedStartDate'
              clearable
              popoverProps={{ zIndex: 10000000, withinPortal: true }}
              {...{ placeholder: 'Select date' }}
            />
            <DatePickerInput
              {...form.getInputProps('endDate')}
              label='End Date'
              data-cy='payrollPeriodEditedEndDate'
              clearable
              popoverProps={{ zIndex: 10000000, withinPortal: true }}
              minDate={dayjs(form.values.startDate).toDate()}
              {...{ placeholder: 'Select date' }}
            />
          </Group>
          <Text>{`Are you sure you want to edit this payroll period?`}</Text>
          <Alert variant='light' color='yellow' title='Attention' icon={<IconInfoCircle />}>
            Editing this payroll period will remove all related data permanently.
          </Alert>
        </Stack>
        <Group position='right' pt='md'>
          <Button
            variant='light'
            onClick={handleCloseForm}
            size='xs'
            radius='md'
            disabled={updateMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size='xs'
            radius='md'
            type='submit'
            loading={updateMutation.isLoading}
            data-cy='payrollPeriodEditSubmitButton'
          >
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default ModalPayrollPeriodEdit;
